import { useState } from "react";
import { theme, btnPrimary, type as text } from "../../theme";
import { PortalLink } from "../../components/PortalLink";
import { api, apiErrorMessage } from "../../api/client";
import { DetailSection } from "../../components/RecordView";
import { input, secondaryBtn } from "./shared";

// Suggested asset classification (backend/src/lib/assetClassification.ts): confidentiality,
// integrity, availability and importance proposed from the records that touch the asset. A member
// accepts, changes or dismisses each; nothing is written without them.

export type ClassificationDimension = "confidentiality" | "integrity" | "availability" | "importance";
export interface ClassificationReason { level: string; text: string; record: { kind: string; id: string; label: string; href: string } }
export interface ClassificationProposal { dimension: ClassificationDimension; current: string | null; proposed: string; reasons: ClassificationReason[]; fingerprint: string }
export interface AssetClassificationView { assetId: string; proposals: ClassificationProposal[]; dismissed: ClassificationProposal[]; unrated: boolean; fillsGap: boolean }
export type ClassificationMap = Record<string, AssetClassificationView>;

export const CIA_LEVELS = ["low", "medium", "high"] as const;
const IMPORTANCE_LEVELS = ["low", "medium", "high", "critical"] as const;
export const DIMENSION_LABELS: Record<ClassificationDimension, string> = { confidentiality: "Confidentiality", integrity: "Integrity", availability: "Availability", importance: "Importance" };
const levelsFor = (d: ClassificationDimension): readonly string[] => (d === "importance" ? IMPORTANCE_LEVELS : CIA_LEVELS);
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** The reason already names the record; the link says where it leads. */
const RECORD_LINKS: Record<string, string> = {
  discovery: "Open the data discovery finding", ropa: "Open the processing record", bia: "Open the business impact analysis",
  supplier: "Open the supplier", asset: "Open the asset", organisation: "Open the organisation profile",
};
const linkStyle: React.CSSProperties = { color: "var(--c-accentText)", textDecoration: "none" };

export async function loadClassifications(): Promise<ClassificationMap> {
  const r = await api.get<{ classifications: ClassificationMap }>("/asset-classification");
  return r.classifications;
}

/** "C high · I — · A medium": the asset's own ratings, short. */
export function ciaSummary(a: { confidentiality?: string | null; integrity?: string | null; availability?: string | null }): string | null {
  if (!a.confidentiality && !a.integrity && !a.availability) return null;
  const v = (x: string | null | undefined) => x ?? "—";
  return `C ${v(a.confidentiality)} · I ${v(a.integrity)} · A ${v(a.availability)}`;
}

/** The register row's note: what the records suggest, in one line. */
export function ClassificationNote({ view }: { view: AssetClassificationView | undefined }) {
  if (!view || !view.proposals.length) return null;
  return (
    <div style={{ ...text.meta, fontWeight: 400, marginTop: 2, color: theme.color.warningText }} title="Open the asset to accept, change or dismiss each suggestion">
      Suggested: {view.proposals.map((p) => `${DIMENSION_LABELS[p.dimension].toLowerCase()} ${p.proposed}`).join(", ")}
    </div>
  );
}

/** The asset view's Classification section: the asset's ratings and each suggestion with the records behind it. */
export function ClassificationPanel({ asset, view, canEdit, onChanged }: {
  asset: { id: string; criticality: string; confidentiality?: string | null; integrity?: string | null; availability?: string | null };
  view: AssetClassificationView | undefined;
  canEdit: boolean;
  onChanged: (view: AssetClassificationView) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<Partial<Record<ClassificationDimension, string>>>({});
  const [dismissing, setDismissing] = useState<ClassificationDimension | null>(null);
  const [reason, setReason] = useState("");

  async function post(dimension: ClassificationDimension, action: "accept" | "dismiss" | "restore", body: Record<string, unknown>) {
    setBusy(`${action}:${dimension}`); setError(null);
    try {
      const r = await api.post<{ classification: AssetClassificationView }>(`/asset-classification/assets/${asset.id}/${action}`, { dimension, ...body });
      setDismissing(null); setReason("");
      onChanged(r.classification);
    } catch (e) { setError(apiErrorMessage(e, "Could not save the classification")); }
    finally { setBusy(null); }
  }

  const current: Record<ClassificationDimension, string | null> = {
    confidentiality: asset.confidentiality ?? null, integrity: asset.integrity ?? null, availability: asset.availability ?? null, importance: asset.criticality,
  };
  const proposals = view?.proposals ?? [];
  const dismissed = view?.dismissed ?? [];

  return (
    <DetailSection title="Classification">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: proposals.length || dismissed.length ? 14 : 0 }}>
        {(Object.keys(DIMENSION_LABELS) as ClassificationDimension[]).map((d) => (
          <div key={d}>
            <div style={text.label}>{DIMENSION_LABELS[d]}</div>
            <div style={{ ...text.body, color: current[d] ? theme.color.text : theme.color.faint }}>{current[d] ? cap(current[d]!) : "Not rated"}</div>
          </div>
        ))}
      </div>
      {!proposals.length && !dismissed.length && (
        <div style={{ ...text.hint, marginTop: 10 }}>No suggestion: nothing in data discovery, the record of processing, the business impact analyses or the supplier register points above these ratings. Set them on the edit form.</div>
      )}
      {proposals.map((p) => {
        const value = choice[p.dimension] ?? p.proposed;
        return (
          <div key={p.dimension} style={{ borderTop: "1px solid var(--c-border)", padding: "10px 0" }}>
            <div style={{ ...text.body, color: theme.color.text, fontWeight: 600 }}>
              {DIMENSION_LABELS[p.dimension]}: {cap(p.proposed)} suggested{p.current ? ` (now ${p.current})` : ""}
            </div>
            <ul style={{ ...text.hint, margin: "4px 0 8px", paddingLeft: 18, lineHeight: 1.5 }}>
              {p.reasons.map((r, i) => (
                <li key={i}>{r.text}. <PortalLink to={r.record.href} style={linkStyle} title={`Open ${r.record.label}`}>{RECORD_LINKS[r.record.kind] ?? "Open the record"}</PortalLink></li>
              ))}
            </ul>
            {canEdit && (
              dismissing === p.dimension ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (optional)" style={{ ...input, flex: "1 1 220px" }} aria-label={`Why the ${DIMENSION_LABELS[p.dimension].toLowerCase()} suggestion is dismissed`} maxLength={1000} />
                  <button type="button" style={secondaryBtn} disabled={!!busy} onClick={() => void post(p.dimension, "dismiss", { reason: reason.trim() || null })}>Dismiss</button>
                  <button type="button" style={secondaryBtn} onClick={() => { setDismissing(null); setReason(""); }}>Cancel</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <select value={value} onChange={(e) => setChoice((c) => ({ ...c, [p.dimension]: e.target.value }))} style={{ ...input, width: "auto" }} aria-label={`${DIMENSION_LABELS[p.dimension]} to record`}>
                    {levelsFor(p.dimension).map((l) => <option key={l} value={l}>{cap(l)}{l === p.proposed ? " (suggested)" : ""}</option>)}
                  </select>
                  <button type="button" style={btnPrimary} disabled={!!busy} onClick={() => void post(p.dimension, "accept", { value })}>{busy === `accept:${p.dimension}` ? "Saving…" : value === p.proposed ? "Accept" : "Record this rating"}</button>
                  <button type="button" style={secondaryBtn} disabled={!!busy} onClick={() => setDismissing(p.dimension)}>Dismiss</button>
                </div>
              )
            )}
          </div>
        );
      })}
      {dismissed.length > 0 && (
        <div style={{ borderTop: "1px solid var(--c-border)", paddingTop: 10 }}>
          <div style={text.label}>Dismissed until the records change</div>
          {dismissed.map((p) => (
            <div key={p.dimension} style={{ ...text.hint, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
              <span>{DIMENSION_LABELS[p.dimension]} {p.proposed} ({p.reasons.length} reason{p.reasons.length === 1 ? "" : "s"})</span>
              {canEdit && <button type="button" style={secondaryBtn} disabled={!!busy} onClick={() => void post(p.dimension, "restore", {})}>Suggest again</button>}
            </div>
          ))}
        </div>
      )}
      {error && <div style={{ ...text.body, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}
    </DetailSection>
  );
}
